import { useEffect } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'

/**
 * Consent Callback Page
 * 
 * This page is the redirect target after admin consent is granted.
 * It automatically closes the popup window and returns the user to the main flow.
 */
export default function ConsentCallback() {
  useEffect(() => {
    const callbackUtc = new Date().toISOString()

    // Extract query params from the redirect
    const params = new URLSearchParams(window.location.search)
    const adminConsent = (params.get('admin_consent') || '').toLowerCase() === 'true'
    const tenant = params.get('tenant') || null
    const state = params.get('state') || null

    // eslint-disable-next-line no-console
    console.log(`[ConsentCallback] UTC=${callbackUtc}, adminConsent=${adminConsent}, state=${state ?? 'null'}`)

    const message = { type: 'admin-consent-complete', adminConsent, tenant, state }

    // Check if we're in a popup window
    if (window.opener) {
      // Notify opener that consent is complete
      try {
        window.opener.postMessage(message, '*')
      } catch (e) {
        // Best-effort - ignore if postMessage fails
        // eslint-disable-next-line no-console
        console.error('Failed to postMessage to opener', e)
      }
      // Also set localStorage as a fallback for the main window to pick up
      try {
        localStorage.setItem('tc_admin_consent', JSON.stringify(message))
        localStorage.setItem('tc_admin_consent_callback_utc', callbackUtc)
      } catch (e) {
        // ignore
      }
      // Do not auto-close the tab - user can close manually after seeing the message
      setTimeout(() => {
        try { window.close() } catch (e) {}
      }, 1500)
    } else {
      // window.opener is null — severed by COOP headers on login.microsoftonline.com.
      // Store in localStorage so the main wizard tab picks it up via storage event.
      try {
        localStorage.setItem('tc_admin_consent', JSON.stringify(message))
        localStorage.setItem('tc_admin_consent_callback_utc', callbackUtc)
      } catch (e) {}
      // Try to close this tab — works if it was opened via window.open()
      setTimeout(() => {
        try { window.close() } catch (e) {}
      }, 1500)
    }
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="h6">Admin consent granted successfully</Typography>
      <Typography variant="body2" color="text.secondary">
        You can close this tab and return to the wizard.
      </Typography>
    </Box>
  )
}
